<script setup lang="ts" generic="T">
import { reactive, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { debouncedWatch, useUrlSearchParams, useBrowserLocation } from '@vueuse/core'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    TableEmpty,
} from '@/components/ui/table'
import { ArrowUp, ArrowDown, Code2 } from 'lucide-vue-next'
import DataTableToolbar from './DataTableToolbar.vue'
import DataTablePagination from './DataTablePagination.vue'
import type {
    IDataTableHeader,
    IPaginationData,
    IDataTableFilter,
    IDataTableUtilityParams,
} from './types'
import { buildQueryFromObject } from './utils'

const props = withDefaults(
    defineProps<{
        headers: IDataTableHeader[]
        items: IPaginationData<T>
        title?: string
        searchable?: boolean
        filters?: IDataTableFilter[]
        emptyMessage?: string
        rowHref?: (row: T) => string
        clickable?: boolean
        flat?: boolean
    }>(),
    {
        searchable: false,
        title: '',
        filters: undefined,
        emptyMessage: 'No data available',
        rowHref: undefined,
        clickable: false,
        flat: false,
    }
)

const emit = defineEmits<{
    'row-click': [row: T]
}>()

const isRowClickable = computed(() => typeof props.rowHref === 'function' || props.clickable)
const hideBodyDividers = computed(() => isRowClickable.value || props.flat)
const showCellHover = computed(() => !isRowClickable.value && !props.flat)

const onRowClick = (row: T) => {
    emit('row-click', row)
    if (props.rowHref) {
        router.visit(props.rowHref(row))
    }
}

const params = computed(() => {
    const params = useUrlSearchParams()
    const nestedObject: Record<string, string | Record<string, string>> = {}

    Object.entries(params).forEach((entry) => {
        if (entry[0].includes('[')) {
            const filter = entry[0].split('[')
            const filterKey = filter[1].replace(']', '')

            if (typeof nestedObject.filter !== 'object') {
                nestedObject.filter = {}
            }
            nestedObject['filter'][filterKey] = entry[1].toString()
        } else {
            nestedObject[entry[0]] = entry[1].toString()
        }
    })

    return nestedObject
})

const resolveDirection = (): 'asc' | 'desc' | undefined => {
    if (params.value.sort_by_direction === undefined) {
        return undefined
    }

    if (params.value.sort_by_direction === 'asc') {
        return 'asc'
    }

    if (params.value.sort_by_direction === 'desc') {
        return 'desc'
    }

    return undefined
}

const resolveFilter = () => {
    if (params.value.filter === undefined) {
        return {
            column: '',
            value: '',
        }
    }

    return {
        column:
            typeof params.value.filter === 'object' && 'column' in params.value.filter
                ? (params.value.filter.column as string)
                : '',
        value:
            typeof params.value.filter === 'object' && 'value' in params.value.filter
                ? (params.value.filter.value as string)
                : '',
    }
}

const paramState = reactive<IDataTableUtilityParams>({
    search:
        params.value.search !== undefined ? params.value.search.toString() : '',
    sort_by:
        params.value.sort_by !== undefined
            ? params.value.sort_by.toString()
            : '',
    sort_by_direction: resolveDirection(),
    filter: resolveFilter(),
})

const onParamUpdate = (newValue: IDataTableUtilityParams) => {
    paramState.search = newValue.search
    paramState.sort_by = newValue.sort_by
    paramState.sort_by_direction = newValue.sort_by_direction
    paramState.filter = newValue.filter
}

debouncedWatch(
    paramState,
    async () => {
        const currentRoute =
            useBrowserLocation().value.origin +
            (useBrowserLocation().value.pathname ?? '')

        if (currentRoute) {
            await router.get(
                currentRoute + '?' + buildQueryFromObject(paramState),
                undefined,
                {
                    preserveState: true,
                    preserveScroll: true,
                }
            )
        }
    },
    { debounce: 220 }
)

const toggleSort = (header: IDataTableHeader) => {
    if (!header.sortable) return

    const sortKey = header.sortableId || header.id

    if (paramState.sort_by !== sortKey) {
        paramState.sort_by = sortKey
        paramState.sort_by_direction = 'asc'
    } else if (paramState.sort_by_direction === 'asc') {
        paramState.sort_by_direction = 'desc'
    } else if (paramState.sort_by_direction === 'desc') {
        paramState.sort_by = ''
        paramState.sort_by_direction = undefined
    }
}

const isSorted = (header: IDataTableHeader) => {
    const sortKey = header.sortableId || header.id
    return paramState.sort_by === sortKey
}

const getSortDirection = (header: IDataTableHeader) => {
    if (!isSorted(header)) return null
    return paramState.sort_by_direction
}

const getAlignClass = (align?: 'center' | 'left' | 'right') => {
    if (align === 'center') return 'text-center'
    if (align === 'right') return 'text-right'
    return 'text-left'
}

const getFlexAlignClass = (align?: 'center' | 'left' | 'right') => {
    if (align === 'center') return 'justify-center'
    if (align === 'right') return 'justify-end'
    return 'justify-start'
}
</script>

<template>
    <div class="w-full min-w-0">
        <!-- Header Section -->
        <div v-if="title || searchable || filters || $slots.toolbar" class="flex flex-wrap items-center justify-between gap-3 py-8">
            <div class="flex items-center gap-[3px] min-w-0">
                <slot name="icon">
                    <Code2 v-if="title" class="size-6 shrink-0 text-new-neutral-800" />
                </slot>
                <h1 v-if="title" class="text-3xl text-new-neutral-800 truncate">{{ title }}</h1>
            </div>
            <DataTableToolbar
                class="flex-wrap"
                :model-value="paramState"
                :searchable="searchable"
                :filters="filters"
                @update:model-value="onParamUpdate"
            >
                <template #toolbar>
                    <slot name="toolbar" />
                </template>
            </DataTableToolbar>
        </div>

        <!-- Table Content -->
        <div
            class="w-full min-w-0"
            :class="{
                'table-empty': items.data.length === 0,
                'hide-body-dividers': hideBodyDividers,
            }"
        >
            <Table>
                <TableHeader>
                    <TableRow class="hover:!bg-transparent">
                        <TableHead
                            v-for="header in headers"
                            :key="header.id"
                            :class="[
                                getAlignClass(header.align),
                                'hover:bg-muted/50 transition-colors',
                                header.sortable && 'cursor-pointer select-none',
                            ]"
                            :style="header.width ? { width: header.width } : undefined"
                            @click="toggleSort(header)"
                        >
                            <div class="flex items-center gap-0.5" :class="getFlexAlignClass(header.align)">
                                {{ header.text }}
                                <template v-if="header.sortable">
                                    <ArrowUp
                                        v-if="getSortDirection(header) === 'asc'"
                                        class="h-4 w-4"
                                    />
                                    <ArrowDown
                                        v-else-if="getSortDirection(header) === 'desc'"
                                        class="h-4 w-4"
                                    />
                                </template>
                            </div>
                        </TableHead>
                    </TableRow>
                </TableHeader>

                <TableBody>
                    <template v-if="items.data.length > 0">
                        <TableRow
                            v-for="(row, rowIndex) in items.data"
                            :key="rowIndex"
                            :class="isRowClickable ? 'cursor-pointer' : 'hover:!bg-transparent'"
                            @click="isRowClickable && onRowClick(row)"
                        >
                            <TableCell
                                v-for="header in headers"
                                :key="header.id"
                                class="align-middle text-sm transition-colors"
                                :class="[
                                    getAlignClass(header.align),
                                    showCellHover && 'hover:bg-muted/50',
                                ]"
                            >
                                <slot
                                    :name="`item-${header.id}`"
                                    :row="row"
                                    :data="(row as any)[header.id]"
                                >
                                    {{ (row as any)[header.id] }}
                                </slot>
                            </TableCell>
                        </TableRow>
                    </template>
                    <template v-else>
                        <TableRow class="!bg-transparent hover:!bg-transparent">
                            <TableCell :colspan="headers.length" class="align-middle">
                                <slot name="empty">
                                    <TableEmpty :message="emptyMessage" />
                                </slot>
                            </TableCell>
                        </TableRow>
                    </template>
                </TableBody>
            </Table>
        </div>

        <!-- Pagination -->
        <DataTablePagination :items="items" :param-state="paramState" />
    </div>
</template>

<style>
.table-empty [data-slot="table-head"]::after,
.hide-body-dividers [data-slot="table-head"]::after {
    display: none !important;
}
</style>
